import Select, { ClearIndicatorProps } from 'react-select';
import { Trans, useLingui } from '@lingui/react/macro';

import { DeliveryOption, PickupPoints } from '@/api';
import Hint from '@/design_system/Hint';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconChevron from '@/icons/Chevron.svg';
import IconCross from '@/icons/Cross.svg';
import IconPlace from '@/icons/Place.svg';
import { Address, formatAddress, useAddresses } from '@/models/address';
import { ShipmentCarrierService, useServicePoint } from '@/models/shipment';
import { createBEMClasses } from '@/utils/classname';
import useDebouncedState from '@/utils/useDebouncedState';

import './PlaceSelect.css';

const { block, element } = createBEMClasses('place-select');

export type ShipmentFormPlace = Address & {
  client: {
    id: string;
    name: string | null;
    deliveryOption: DeliveryOption | null;
    sendcloudServicePointId: string | null;
    organizationId: string;
  } | null;
  entity: {
    id: string;
    name: string;
    placeConfig: {
      pickupPoints?: PickupPoints;
    } | null;
    workshopConfig?: {
      nonDigitalized: boolean;
    } | null;
  } | null;
};

export const newShipmentFormPlace = (
  address: Address,
  client: ShipmentFormPlace['client'],
  entity: ShipmentFormPlace['entity']
): ShipmentFormPlace => ({
  ...address,
  client,
  entity,
});

const getPlaceName = (place: ShipmentFormPlace) => place.client?.name ?? place.entity?.name ?? '-';

export const PlaceSelect = ({
  type,
  label,
  placeholder,
  selected,
  setSelected,
  isDisabled,
  carrierService,
}: {
  type: 'origin' | 'destination';
  label: string;
  placeholder: string;
  selected?: ShipmentFormPlace;
  setSelected: (address?: ShipmentFormPlace) => void;
  isDisabled?: boolean;
  carrierService?: ShipmentCarrierService;
}) => {
  const { t } = useLingui();
  const [query, debouncedQuery, setQuery] = useDebouncedState<string>('', 500);

  const {
    data: { addresses } = { addresses: [] },
    isPending,
    isFetching,
  } = useAddresses(
    {
      limit: 10,
      type,
      search: debouncedQuery ?? '',
    },
    {
      keepPreviousData: true,
    }
  );

  if (type === 'origin' && addresses.length === 1 && !debouncedQuery && !selected) {
    setSelected(addresses[0]);
  }

  const servicePoint = useDestinationPickupPoint({ selected, carrierService });

  const typeLabel = {
    origin: t({ id: 'shipments.new.origin.label', message: 'Origin' }),
    destination: t({ id: 'shipments.new.destination.label', message: 'Destination' }),
  }[type];

  return (
    <Stack gap="0.25rem" style={{ flex: 1, maxWidth: '100%' }} ariaLabel={typeLabel}>
      <div className="label-100-medium">{label}</div>
      <Select
        classNamePrefix={block()}
        key={type}
        isDisabled={isDisabled || (type === 'origin' && addresses.length === 1 && !debouncedQuery)}
        isClearable={type === 'destination' || addresses.length > 1}
        components={{
          DropdownIndicator: () => (
            <div className={element('chevron')}>
              <IconChevron down />
            </div>
          ),
          ClearIndicator: ({
            innerProps: { ref, ...restInnerProps },
          }: ClearIndicatorProps<ShipmentFormPlace, false>) => (
            <div {...restInnerProps} ref={ref} className={element('clear')}>
              <IconCross />
            </div>
          ),
        }}
        isLoading={isPending || isFetching}
        placeholder={<InlineText color="disabled">{placeholder}</InlineText>}
        isSearchable
        value={selected}
        onChange={(value) => setSelected(value ?? undefined)}
        inputValue={query}
        onInputChange={(query, event) => {
          if (!['input-blur', 'menu-close'].includes(event.action)) {
            setQuery(query);
          }
        }}
        getOptionValue={getPlaceName} // Based on https://github.com/JedWatson/react-select/issues/3869, adding this props removes the error 'A component is changing a controlled input to be uncontrolled.'
        unstyled
        filterOption={null} // Avoid react-select to filter options only based on their label
        options={addresses}
        noOptionsMessage={() => (
          <Paragraph style={{ padding: '0.5rem', background: 'var(--color-neutral-0)' }}>
            <Trans id="shipments.new.address.no-results">No results</Trans>
          </Paragraph>
        )}
        loadingMessage={() => (
          <InlineText style={{ padding: '0.5rem', background: 'var(--color-neutral-0)' }}>
            <Trans id="shipments.new.address.loading">Loading...</Trans>
          </InlineText>
        )}
        formatOptionLabel={(value) => (
          <PlaceSelectInput
            id={value.id}
            name={getPlaceName(value)}
            formatted={formatAddress(value)}
          />
        )}
      />

      {carrierService === 'point_relais' && servicePoint && (
        <Hint>
          <Stack row alignItems="center" gap="0.5rem" flexWrap="nowrap">
            <IconPlace
              start={type === 'origin'}
              end={type === 'destination'}
              style={{ fontSize: '1.25rem' }}
            />
            <>
              <Trans id="shipments.new.service_point.via-mondial-relay">(Via Mondial Relay)</Trans>{' '}
              {servicePoint.name} - {servicePoint.address.line1} {servicePoint.address.zipCode}{' '}
              {servicePoint.address.city}
            </>
          </Stack>
        </Hint>
      )}
    </Stack>
  );
};

const PlaceSelectInput = (address: { id: string; name?: string; formatted?: string }) => {
  return (
    <p className={element('input', {}, 'text-ellipsis')}>
      {!address.name && !address.formatted ? (
        <Trans id="shipments.new.address.no-address-available">No address available</Trans>
      ) : (
        <>
          <span className={element('input__name')}>{address.name}</span>{' '}
          {!!address.formatted && (
            <span className={element('input__address')}>({address.formatted})</span>
          )}
        </>
      )}
    </p>
  );
};

const useDestinationPickupPoint = ({
  selected,
  carrierService,
}: {
  selected?: ShipmentFormPlace;
  carrierService?: ShipmentCarrierService;
}) => {
  let selectedPickupPoint = undefined;

  if (carrierService === 'point_relais') {
    if (selected?.entity) {
      selectedPickupPoint = selected.entity.placeConfig?.pickupPoints?.sendcloud?.mondial_relay;
    } else if (selected?.client) {
      selectedPickupPoint =
        selected.client.deliveryOption === 'pickupPoint_mondialRelay'
          ? selected.client.sendcloudServicePointId
          : undefined;
    }
  }

  const { data: servicePoint } = useServicePoint(selectedPickupPoint ?? undefined);

  return servicePoint;
};
